import fetchClient from './fetchClient';

const cfg = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });

// ── Dashboard ─────────────────────────────────────────────────────────────────

export const getOKRDashboard = async () => {
    const res = await fetchClient.get('/okr/dashboard', cfg()).catch((): null => null);
    return res?.data ?? null;
};

// ── Available MetricBridge metrics ────────────────────────────────────────────

export const getAvailableMetrics = async () => {
    const res = await fetchClient.get('/okr/metrics/available', cfg()).catch((): null => null);
    return res?.data ?? null;
};

// ── Commercial activity stats (calls/emails/meetings) ─────────────────────────
// Only returns data when the owner has the OKR option activated (403 otherwise).

export const getActivityStats = async (
    periodicity: 'daily' | 'weekly' | 'monthly' | 'quarterly',
    offset: number = 0
) => {
    const res = await fetchClient
        .get('/okr/metrics/activity-stats', { ...cfg(), params: { periodicity, offset } })
        .catch((): null => null);
    return res?.data ?? null;
};

// ── Objectives ────────────────────────────────────────────────────────────────

export const getObjectives = async (params?: { status?: string; priority?: string }) => {
    const res = await fetchClient.get('/okr/objectives', { ...cfg(), params }).catch((): null => null);
    return res?.data ?? null;
};

export const getObjectiveDetail = async (id: string) => {
    const res = await fetchClient.get(`/okr/objectives/${id}`, cfg()).catch((): null => null);
    return res?.data ?? null;
};

export const createObjective = async (body: Record<string, any>) => {
    const res = await fetchClient.post('/okr/objectives', body, cfg()).catch((): null => null);
    return res?.data ?? null;
};

export const updateObjective = async (id: string, body: Record<string, any>) => {
    const res = await fetchClient.put(`/okr/objectives/${id}`, body, cfg()).catch((): null => null);
    return res?.data ?? null;
};

export const deleteObjective = async (id: string) => {
    const res = await fetchClient.delete(`/okr/objectives/${id}`, cfg()).catch((): null => null);
    return res?.data ?? null;
};

// ── KPIs ──────────────────────────────────────────────────────────────────────

export const getKPIs = async (objectiveId?: string) => {
    const params = objectiveId ? { objectiveId } : undefined;
    const res = await fetchClient.get('/okr/kpis', { ...cfg(), params }).catch((): null => null);
    return res?.data ?? null;
};

export const createKPI = async (body: Record<string, any>) => {
    const res = await fetchClient.post('/okr/kpis', body, cfg()).catch((): null => null);
    return res?.data ?? null;
};

export const updateKPI = async (id: string, body: Record<string, any>) => {
    const res = await fetchClient.put(`/okr/kpis/${id}`, body, cfg()).catch((): null => null);
    return res?.data ?? null;
};

export const deleteKPI = async (id: string) => {
    const res = await fetchClient.delete(`/okr/kpis/${id}`, cfg()).catch((): null => null);
    return res?.data ?? null;
};

// ── Action Plans ──────────────────────────────────────────────────────────────

export const getActionPlans = async (objectiveId?: string) => {
    const params = objectiveId ? { objectiveId } : undefined;
    const res = await fetchClient.get('/okr/plans', { ...cfg(), params }).catch((): null => null);
    return res?.data ?? null;
};

export const createActionPlan = async (body: Record<string, any>) => {
    const res = await fetchClient.post('/okr/plans', body, cfg()).catch((): null => null);
    return res?.data ?? null;
};

export const updateActionPlan = async (id: string, body: Record<string, any>) => {
    const res = await fetchClient.put(`/okr/plans/${id}`, body, cfg()).catch((): null => null);
    return res?.data ?? null;
};

export const deleteActionPlan = async (id: string) => {
    const res = await fetchClient.delete(`/okr/plans/${id}`, cfg()).catch((): null => null);
    return res?.data ?? null;
};

// ── Tasks ─────────────────────────────────────────────────────────────────────

export const getOKRTasks = async (params?: { objectiveId?: string; status?: string; assigneeId?: string; page?: number; limit?: number }) => {
    const res = await fetchClient.get('/okr/tasks', { ...cfg(), params }).catch((): null => null);
    return res?.data ?? null;
};

export const createOKRTask = async (body: Record<string, any>) => {
    const res = await fetchClient.post('/okr/tasks', body, cfg()).catch((): null => null);
    return res?.data ?? null;
};

export const updateOKRTask = async (id: string, body: Record<string, any>) => {
    const res = await fetchClient.put(`/okr/tasks/${id}`, body, cfg()).catch((): null => null);
    return res?.data ?? null;
};

export const updateOKRTaskStatus = async (id: string, status: string, extra?: { blockedReason?: string; businessImpact?: Record<string, any> }) => {
    const res = await fetchClient.patch(`/okr/tasks/${id}/status`, { status, ...extra }, cfg()).catch((): null => null);
    return res?.data ?? null;
};

export const deleteOKRTask = async (id: string) => {
    const res = await fetchClient.delete(`/okr/tasks/${id}`, cfg()).catch((): null => null);
    return res?.data ?? null;
};

// ── Alerts ────────────────────────────────────────────────────────────────────

export const getOKRAlerts = async (acknowledged?: boolean) => {
    const params = acknowledged !== undefined ? { acknowledged: String(acknowledged) } : undefined;
    const res = await fetchClient.get('/okr/alerts', { ...cfg(), params }).catch((): null => null);
    return res?.data ?? null;
};

export const createOKRAlert = async (body: Record<string, any>) => {
    const res = await fetchClient.post('/okr/alerts', body, cfg()).catch((): null => null);
    return res?.data ?? null;
};

export const acknowledgeOKRAlert = async (id: string) => {
    const res = await fetchClient.patch(`/okr/alerts/${id}/acknowledge`, {}, cfg()).catch((): null => null);
    return res?.data ?? null;
};

export const deleteOKRAlert = async (id: string) => {
    const res = await fetchClient.delete(`/okr/alerts/${id}`, cfg()).catch((): null => null);
    return res?.data ?? null;
};
